import React, { useState, useEffect } from 'react';
import { Portal, Dialog, TextInput, Button, Text, useTheme, IconButton } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { apiFetch, createBillingPortalSession } from '../api/apiService';
import { API_ENDPOINTS } from '../api/config';
import { View } from 'react-native';

// Stripe publishable key (web)
const STRIPE_PUBLISHABLE_KEY = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || 'pk_test_51RWIRNH2XrByySGIVg3xqGs1t9gHur1HD5FyalpJWz32f2VIdybZfBokm7YLgsjUN4afVFpHvzlH31JtABzwIKNF00x8mZ2PA7';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export interface EditSubscriptionModalProps {
  visible: boolean;
  subscription: any;
  onDismiss: () => void;
  onSuccess: () => void;
}

export function EditSubscriptionModal({ visible, subscription, onDismiss, onSuccess }: EditSubscriptionModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize form when modal opens or subscription changes
  useEffect(() => {
    if (subscription) setQuantity(subscription.licenses.toString());
    setClientSecret(null);
    setError(null);
  }, [visible, subscription]);

  // Quantity input handlers similar to BuyLicenseModal.web
  const handleQuantityChange = (text: string) => {
    const positiveInteger = text.replace(/[^0-9]/g, '');
    setQuantity(positiveInteger === '' ? '1' : positiveInteger);
  };
  const incrementQuantity = () => {
    const currentQty = parseInt(quantity, 10);
    setQuantity((currentQty + 1).toString());
  };
  const decrementQuantity = () => {
    const currentQty = parseInt(quantity, 10);
    if (currentQty > 1) {
      setQuantity((currentQty - 1).toString());
    }
  };

  // Call backend to update subscription quantity
  const handleUpdate = async () => {
    // Redirect to Stripe Billing Portal to manage subscription
    try {
      setLoading(true);
      const returnUrl = window.location.origin + '/dashboard/manage-licenses';
      const portalResp = await createBillingPortalSession(
        returnUrl,
        returnUrl,
        false
      );
      if (portalResp.ok && portalResp.data.url) {
        window.location.href = portalResp.data.url;
      } else {
        setError('Failed to open billing portal');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  // Nested payment confirmation dialog wrapped by Elements
  const PaymentConfirmationDialog: React.FC<{ visible: boolean }> = ({ visible }) => {
    const stripe = useStripe();
    const elements = useElements();
    const theme = useTheme();
    
    const handleConfirm = async () => {
      if (!stripe || !elements) {
        setError('Payment system is not ready. Please try again.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { error: confirmError } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.origin + '/dashboard' },
        });
        if (confirmError) {
          setError(confirmError.message || 'Payment confirmation failed');
        } else {
          onSuccess();
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    return (
      <Dialog visible={visible} onDismiss={onDismiss} style={{ maxWidth: 600, width: '60%', alignSelf: 'center' }}>
        <Dialog.Title>Complete Authentication</Dialog.Title>
        <Dialog.Content>
          <PaymentElement />
          {error && <Text style={{ color: theme.colors.error, marginTop: 12 }}>{error}</Text>}
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={onDismiss} disabled={loading}>Cancel</Button>
          <Button onPress={handleConfirm} loading={loading}>Confirm</Button>
        </Dialog.Actions>
      </Dialog>
    );
  };

  // No in-app payment here anymore; redirect to portal on save

  // Default: show portal management CTA, no quantity fields
  return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss} style={{ maxWidth: 600, width: '60%', alignSelf: 'center' }}>
        <Dialog.Title>Manage Subscription</Dialog.Title>
        <Dialog.Content>
          <View style={{ height: 8 }} />
          {error && <Text style={{ color: theme.colors.error, marginTop: 12 }}>{error}</Text>}
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={onDismiss} disabled={loading}>{t('cancel', 'Cancel')}</Button>
          <Button onPress={handleUpdate} loading={loading} icon="open-in-new">{t('manageInStripe', 'Manage in Stripe')}</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
} 